"use client";

import React, { createContext, useContext, useState, useCallback, useRef } from "react";

type AudioContextType = {
  isMuted: boolean;
  toggleMute: () => void;
  playTick: () => void;
  playWhoosh: () => void;
  playChime: () => void;
  playSting: () => void;
};

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export function AudioProvider({ children }: { children: React.ReactNode }) {
  const [isMuted, setIsMuted] = useState(true);

  // Use AudioContext or simple Audio elements. Synthesizing for placeholders to avoid missing assets.
  const audioCtx = useRef<AudioContext | null>(null);

  const initAudio = () => {
    if (!audioCtx.current) {
      audioCtx.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  };

  const playOscillator = useCallback((type: OscillatorType, frequency: number, duration: number, gainValue: number) => {
    if (isMuted) return;
    initAudio();
    if (!audioCtx.current) return;
    
    const osc = audioCtx.current.createOscillator();
    const gain = audioCtx.current.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, audioCtx.current.currentTime);
    osc.connect(gain);
    gain.connect(audioCtx.current.destination);
    
    gain.gain.setValueAtTime(gainValue, audioCtx.current.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.current.currentTime + duration);
    
    osc.start();
    osc.stop(audioCtx.current.currentTime + duration);
  }, [isMuted]);

  const playTick = useCallback(() => playOscillator("sine", 800, 0.05, 0.1), [playOscillator]);
  const playWhoosh = useCallback(() => playOscillator("triangle", 150, 0.3, 0.05), [playOscillator]);
  const playChime = useCallback(() => playOscillator("sine", 1200, 0.5, 0.1), [playOscillator]);
  const playSting = useCallback(() => {
    playOscillator("square", 440, 0.1, 0.1);
    setTimeout(() => playOscillator("square", 554, 0.1, 0.1), 100);
    setTimeout(() => playOscillator("square", 659, 0.4, 0.1), 200);
  }, [playOscillator]);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  return (
    <AudioContext.Provider value={{ isMuted, toggleMute, playTick, playWhoosh, playChime, playSting }}>
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error("useAudio must be used within an AudioProvider");
  }
  return context;
}
